import { AppTypeId } from '@ps/platform-message-framework/common/AppTypeId';
import { ProductId } from '@ps/platform-message-framework/common/ProductId';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Header } from '@swf/core/enums';
import type { PSNPartnerNotification } from '@swf/core/types/Notification';
import { fetchJson } from '@swf/core/utils/api';

import { select } from '../store';

interface CTAButton {
  id: string;
  label: string;
  kind: string;
}

interface Template {
  title: string;
  content: string;
  actions: CTAButton[];
}

interface TemplateContentState extends Partial<Template> {
  loading: boolean;
  error?: Error | string;
}

const fetchTemplateContent = async (
  headers: Record<string, string>,
  input: {
    templateId: PSNPartnerNotification['templateId'];
    context: PSNPartnerNotification['templateContext'];
    platform: string;
    locale: string;
    siteId: string;
  },
  apiDomain: string
) => {
  const response = await fetchJson(`${apiDomain}/pam/tds/graphql`, {
    headers,
    body: JSON.stringify({
      query:
        'query RenderTemplate($input: TemplateRenderInput!) { renderTemplate(input: $input) { title content actions { id label kind } } }',
      variables: { input }
    }),
    method: 'POST'
  });
  if (response.errors) {
    throw new Error(response.errors.map((error: { message: string }) => error.message).join(', '));
  }
  return response.data?.renderTemplate as Template;
};

const useTemplateContent = ({
  templateId,
  templateContext
}: PSNPartnerNotification): TemplateContentState => {
  const [template, setTemplate] = useState<Template>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | string>();

  const { apiDomain = '' } = useSelector(select.config.get);
  const { installId, idpToken } = useSelector(select.session.get);
  const os = useSelector(select.device.getOS);
  const device = useSelector(select.device.getId);
  const platform = useSelector(select.device.getPlatform);
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const brand = useSelector(select.cms.getBrandId);
  const site = useSelector(select.cms.getSiteId);
  const locale = useSelector(select.locale.get);

  const headers = useMemo(
    () => ({
      [Header.FiSiteId]: `${site}`,
      [Header.FiDeviceId]: `${device}`,
      [Header.FiOsId]: `${os}`,
      [Header.FiBrandId]: `${brand}`,
      [Header.FiPlatformId]: `${platform}`,
      [Header.FiInstallId]: `${installId}`,
      [Header.FiAppTypeId]: `${AppTypeId.RAM}`,
      [Header.FiProductId]: `${ProductId.POKER}`,
      'content-type': 'application/json',
      ...(idpToken && { authorization: `Bearer ${idpToken}` })
    }),
    [site, device, os, brand, platform, installId, idpToken]
  );

  const input = useMemo(
    () => ({
      templateId,
      platform: isDesktopEmbedded ? 'DESKTOP' : isMobileEmbedded ? 'MOBILE' : 'WEB',
      locale,
      siteId: Sites[site],
      context: templateContext
    }),
    [templateId, isDesktopEmbedded, isMobileEmbedded, locale, site, templateContext]
  );

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        const result = await fetchTemplateContent(headers, input, apiDomain);
        setTemplate(result);
      } catch (error) {
        setError(error as Error);
      } finally {
        setLoading(false);
      }
    };
    if (idpToken) {
      loadContent();
    }
  }, [apiDomain, headers, idpToken, input, templateId]);

  return { ...template, loading, error };
};

export default useTemplateContent;
